import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, ShieldCheck, Star, UserCircle2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { formatGBPfromPence, calculateOrderTotalPence } from '@/lib/format';
import { initiatePayment } from '@/lib/payments';

/**
 * Bottom-sheet checkout modal — Cashflows Hosted Payment Page.
 *
 * Order of content (per product brief):
 *   1. Skill question — moved out of the main page scroll. ANY selected
 *      answer unlocks payment (DCMS Code: correctness is never checked).
 *   2. Account step — if signed out, show Create-account / Sign-in CTAs.
 *      Parent persists quantity in sessionStorage so the round-trip doesn't
 *      lose the user's selection.
 *   3. Pay button — disabled unless answer selected AND user signed in AND
 *      no account block reason applies. Tapping Pay creates the Cashflows
 *      intent and full-page-redirects to Cashflows' hosted checkout.
 *
 * Dismissing the sheet does NOT reset ticket quantity (kept in parent state).
 * Selected answer is local to the sheet — closing + reopening starts over on
 * the question, matching typical checkout flows.
 */
export const CheckoutSheet = ({
  open,
  onOpenChange,
  competition,
  quantity,
  user,
  blockedReason,
  returnTo,
}) => {
  const [skillAnswer, setSkillAnswer] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!open) setSkillAnswer(null);
  }, [open]);

  const totalPence = calculateOrderTotalPence(competition?.ticket_price_gbp, quantity);
  const hasAnswer = skillAnswer != null;
  const canPay = Boolean(user) && hasAnswer && !blockedReason && !paying;

  const doPay = async () => {
    if (!canPay) return;
    setPaying(true);
    try {
      await initiatePayment({
        competition,
        quantity,
        user,
        skillAnswerIndex: skillAnswer,
      });
      // initiatePayment full-page-redirects to Cashflows on success — the
      // page unmounts before this line runs.
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Could not start checkout');
      setPaying(false);
    }
  };

  const question = competition?.skill_question || '';
  const options = competition?.skill_options || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="bg-surface text-[color:var(--pd-text)] border-t-2 border-[color:var(--pd-accent-yellow)]/50 rounded-t-3xl max-h-[92dvh] overflow-y-auto p-0"
        data-testid="checkout-sheet"
      >
        <div
          className="mx-auto max-w-xl px-5 md:px-6 pt-4"
          style={{
            paddingTop: 'max(1rem, env(safe-area-inset-top))',
            paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))',
          }}
        >
          <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-muted-sage/40" aria-hidden />

          <SheetTitle asChild>
            <div>
              <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">Enter the draw</div>
              <div className="font-display text-3xl leading-tight mt-1" data-testid="checkout-sheet-title">
                {competition?.title || 'Confirm your entry'}
              </div>
            </div>
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-sage mt-2">
            {quantity} ticket{quantity === 1 ? '' : 's'} &middot; <span className="font-mono text-[color:var(--pd-text)]">{formatGBPfromPence(totalPence)}</span>
          </SheetDescription>

          {/* 1. Skill question */}
          <section className="mt-6 rounded-2xl border border-pd bg-[color:var(--pd-bg)] p-4 md:p-5" data-testid="checkout-skill-block">
            <div className="font-mono text-[10px] text-muted-sage uppercase tracking-widest mb-1">Skill question &mdash; required</div>
            <div className="font-display text-xl leading-tight">{question}</div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {options.map((opt, i) => {
                const selected = skillAnswer === i;
                return (
                  <label
                    key={opt}
                    className={`cursor-pointer flex items-center gap-3 rounded-xl px-3 py-3 text-sm border-2 transition-colors ${selected ? 'border-[color:var(--pd-accent-yellow)] bg-[color:var(--pd-accent-yellow)]/10 text-[color:var(--pd-text)]' : 'border-pd text-muted-sage hover:border-[color:var(--pd-accent-yellow)]/50'}`}
                    data-testid={`checkout-skill-option-${i}`}
                  >
                    <input
                      type="radio"
                      name="checkout-skill"
                      className="accent-[color:var(--pd-accent-yellow)]"
                      checked={selected}
                      onChange={() => setSkillAnswer(i)}
                    />
                    {opt}
                  </label>
                );
              })}
            </div>
          </section>

          {/* 2. Account */}
          <section className="mt-4 rounded-2xl border border-pd bg-[color:var(--pd-bg)] p-4 md:p-5" data-testid="checkout-account-block">
            <div className="font-mono text-[10px] text-muted-sage uppercase tracking-widest mb-2">Entering as</div>
            {user ? (
              <div className="flex items-center gap-3" data-testid="checkout-signed-in">
                <UserCircle2 size={22} className="text-[color:var(--pd-gold)] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-[color:var(--pd-text)] truncate">{user.full_name}</div>
                  <div className="text-xs text-muted-sage truncate">{user.email}</div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-sage">
                  You need an account to enter. It takes under a minute and lets us protect players in line with the DCMS Prize Draws &amp; Competitions Code.
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Link
                    to={`/register?returnTo=${encodeURIComponent(returnTo)}`}
                    className="text-center bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-3 py-2.5 text-sm font-medium"
                    data-testid="checkout-sheet-register"
                  >
                    Create account
                  </Link>
                  <Link
                    to={`/login?returnTo=${encodeURIComponent(returnTo)}`}
                    className="text-center border border-[color:var(--pd-accent-yellow)] text-[color:var(--pd-accent-yellow)] rounded-xl px-3 py-2.5 text-sm font-medium"
                    data-testid="checkout-sheet-signin"
                  >
                    Sign in
                  </Link>
                </div>
                <p className="text-[11px] text-muted-sage mt-2">
                  Your selected {quantity} ticket{quantity === 1 ? '' : 's'} will be waiting for you when you get back.
                </p>
              </>
            )}
            {blockedReason ? (
              <div className="mt-3 rounded-xl border border-[color:var(--pd-gold)]/50 bg-surface p-3 text-xs" data-testid="checkout-sheet-blocked">
                {blockedReason}{' '}
                <Link to="/account" className="text-[color:var(--pd-accent-yellow)] underline">Manage in account</Link>
              </div>
            ) : null}
          </section>

          {/* Instant-win notice */}
          {competition?.instant_win ? (
            <div className="mt-4 rounded-2xl border border-[color:var(--pd-gold)]/50 bg-[color:var(--pd-bg)] p-4 text-xs" data-testid="checkout-instant-win-notice">
              <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest mb-1">Instant-win draw</div>
              Credit cards are not accepted on instant-win draws under the DCMS Code (clause 1.3). Please pay by debit card only. Any credit-card payment received will be automatically refunded and no tickets will be issued.
            </div>
          ) : null}

          {/* Trust strip */}
          <div className="mt-4 grid grid-cols-3 gap-2" data-testid="checkout-sheet-trust">
            <div className="flex items-center gap-1.5 bg-[color:var(--pd-bg)] border border-pd rounded-xl px-2 py-2">
              <Lock size={12} className="text-[color:var(--pd-accent-yellow)] shrink-0" />
              <span className="text-[10px] leading-tight">Secure payment</span>
            </div>
            <div className="flex items-center gap-1.5 bg-[color:var(--pd-bg)] border border-pd rounded-xl px-2 py-2">
              <ShieldCheck size={12} className="text-[color:var(--pd-accent-yellow)] shrink-0" />
              <span className="text-[10px] leading-tight">Draw goes ahead</span>
            </div>
            <div className="flex items-center gap-1.5 bg-[color:var(--pd-bg)] border border-pd rounded-xl px-2 py-2">
              <Star size={12} className="text-[color:var(--pd-accent-yellow)] shrink-0" />
              <span className="text-[10px] leading-tight">Independently verified</span>
            </div>
          </div>

          {/* 3. Pay */}
          <button
            type="button"
            onClick={doPay}
            disabled={!canPay}
            className="mt-5 w-full bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-4 text-base md:text-lg font-bold tracking-tight shadow-xl shadow-black/30 ring-2 ring-[color:var(--pd-accent-yellow)]/40 hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:ring-0"
            data-testid="checkout-sheet-pay"
          >
            {paying
              ? 'Redirecting to secure checkout…'
              : !user
                ? 'Sign in or create an account to enter'
                : !hasAnswer
                  ? 'Select an answer to continue'
                  : blockedReason
                    ? 'Cannot enter right now'
                    : `Pay ${formatGBPfromPence(totalPence)}`}
          </button>
          <p className="text-[11px] text-muted-sage text-center mt-3">
            Debit &amp; credit cards &middot; Apple Pay &middot; 3D Secure &middot; Powered by Cashflows
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CheckoutSheet;
